import { ProductGame, ProductType, PurchasabilityStatus } from "@/@types/common";
import { VirtualCurrencyCode } from "@/@types/currencies";

//Since Card have different types
export enum CardProductType {
  CHAMPION = "CHAMPION",
  CHAMPION_SKIN = "CHAMPION_SKIN",
  BUNDLE = "BUNDLE",
  WEAPON = "WEAPON",
  WEAPON_SKIN = "WEAPON_SKIN",
  SPRAY = "SPRAY",
  PLAYER_CARD = "PLAYER_CARD",
  MAP = "MAP",
  GAME_MODE = "GAME_MODE",
  CHARM = "CHARM",
  CHARACTER = "CHARACTER",
  ABILITY = "ABILITY",
}

//Since Cards might use these two types differently

export enum CardProductGame {
  wildrift = "wildrift",
  leagueoflegends = "leagueoflegends",
  valorant = "valorant",
  legendsofruneterra = "legendsofruneterra",
}

// Creator = Affiliate
export type CreatorResponse = {
  creator: {
    name: string;
  };
};

export enum CollectionDisplayType {
  FLEX = "flex",
  DEFAULT = "default",
}

export type DynamicCollection = {
  latestProducts?: ProductResponse[];
  discountedProductsByProductType?: {
    [ProductType.CHAMPION]: ProductResponse[];
    [ProductType.CHAMPION_SKIN]: ProductResponse[];
  };
};

// // Multi item
export type MultiProductResponse = {
  products: ProductResponse[];
  sections: SectionResponse;
  dynamic?: boolean;
  dynamicCollection?: DynamicCollection;
  bannerUrl?: string;
  bannerTitle?: string;
  logoUrl?: string;
  path?: string;
};

export type AuthedMultiProductResponse = MultiProductResponse & {
  ownedItems?: OwnedItem[];
};

// Single item
export type ProductResponse = {
  game: ProductGame;
  id: string;
  type: ProductType;
  status: PurchasabilityStatus;
  section: string;
  prices: [
    {
      originalPrice: {
        currency: VirtualCurrencyCode;
        cost: number;
      };
      finalPrice: {
        currency: VirtualCurrencyCode;
        cost: number;
      };
      discounted: boolean;
      currency: VirtualCurrencyCode;
      saleEnd: string | null; // default iso format
    },
  ];
  contents: []; // Not in the scope of BETA
};

export type ProductIdentity = {
  type?: "ProductIdentity";
  gameId: ProductGame;
  productType: string;
  productId: string;
};

export interface EnrichedProductIdentity extends ProductIdentity {
  cost?: number;
  price?: number;
  ownerShipStatus?: boolean;
}

// Section response
export type SectionResponse = Record<string, ProductIdentity[]>;

export type OwnedItem = {
  gameId: string;
  productType: string;
  productId: string;
};

export type ProductAsset =
  | readonly ({
      url: string | null;
      type: string | null;
      productId?: string;
      width?: number;
      height?: number;
      dominantColor: string | null;
    } | null)[]
  | null;

export type Product = {
  productId?: string | null;
  name: string | null;
  type: string | null;
  slug: string | null;
  itemId: string | null;
  locale: string | null;
  description: string | null;
  assets?: ProductAsset;
};

export type ProductInBundle = {
  type: CardProductType;
  id: string;
  gameId: ProductGame;
  defaultChromaId: string;
  levels: {
    id: string;
    name: string;
  }[];
  name: string;
  assets: ProductAsset;
  bundleId: string;
  bundleUrl: string;
  showBundleLink?: boolean;
  availability: {
    maxPurchase?: number;
    owned?: number;
  };
};

export type Section = {
  key: string;
  type: string;
  products: ProductIdentity[];
  sectionName: string;
  headerKey: string;
};

export const sectionTypes: Record<string, Section> = {
  featuredLatest: {
    key: "featured-latest-products",
    type: CollectionDisplayType.FLEX,
    products: [],
    sectionName: "featured-latest",
    headerKey: "dbuy.page.collection.section.title.featured",
  },
  allLatest: {
    key: "all-latest-products",
    type: CollectionDisplayType.DEFAULT,
    products: [],
    sectionName: "all-latest",
    headerKey: "",
  },
  skinSales: {
    key: "all-skin-sales",
    type: CollectionDisplayType.DEFAULT,
    products: [],
    sectionName: "all-skin-sales",
    headerKey: "dbuy.page.collection.sale.general",
  },
  featuredSkinSales: {
    key: "featured-skin-sales",
    type: CollectionDisplayType.FLEX,
    products: [],
    sectionName: "featured-skin-sales",
    headerKey: "dbuy.page.collection.sale.skin",
  },
  restSkinSales: {
    key: "rest-skin-sales",
    type: CollectionDisplayType.DEFAULT,
    products: [],
    sectionName: "rest-skin-sales",
    headerKey: "",
  },
  allChampionSales: {
    key: "all-champion-sales",
    type: CollectionDisplayType.DEFAULT,
    products: [],
    sectionName: "all-champion-sales",
    headerKey: "dbuy.page.collection.sale.champion",
  },
  featuredProducts: {
    key: "featured-products",
    type: CollectionDisplayType.FLEX,
    products: [],
    sectionName: "featured",
    headerKey: "dbuy.page.collection.section.title.featured",
  },
  allProducts: {
    key: "all-products",
    type: CollectionDisplayType.DEFAULT,
    products: [],
    sectionName: "all",
    headerKey: "dbuy.page.collection.section.title.all_products",
  },
};
